import { useState } from 'react';
import { useLearningPlanMutation, usePlanGroup } from '../../api/learning-plans';
import { Modal } from '../../components/Modal';
import type { LearningPlan, RecordPlanPaymentInput } from '../../types/learning-plans';
import { learningPlanError } from './labels';

export function RecordPlanPayment({ request, onClose, onDone }: {
  request: LearningPlan;
  onClose: () => void;
  onDone: () => void;
}): React.ReactNode {
  const group = usePlanGroup(request.id);
  if (!group.data) return <Modal open lang="ar" dir="rtl" className="learning-plans-ar" title="تسجيل دفعة وتأكيد الحصص" onClose={onClose}>
    {group.isError ? <p role="alert">{learningPlanError(group.error)} <button type="button" className="btn btn-secondary" onClick={() => { void group.refetch(); }}>إعادة المحاولة</button></p>
      : <p role="status">جارٍ تحميل الخطط المرتبطة…</p>}
  </Modal>;
  const current = group.data.find((r) => r.id === request.id) ?? request;
  return <RecordPlanPaymentForm request={current} requests={group.data} onClose={onClose} onDone={onDone} />;
}

function RecordPlanPaymentForm({ request, requests, onClose, onDone }: {
  request: LearningPlan;
  requests: LearningPlan[];
  onClose: () => void;
  onDone: () => void;
}): React.ReactNode {
  const mutation = useLearningPlanMutation();
  const group = requests.filter((r) => r.groupId === request.groupId);
  const outstanding = group.filter((r) => r.status !== 'paid' && !r.providerRef);
  const total = outstanding.reduce((sum, r) => sum + r.amountCents, 0);
  const [amount, setAmount] = useState((total / 100).toFixed(2));
  const [method, setMethod] = useState<'bank_transfer' | 'cash'>('bank_transfer');
  const [reference, setReference] = useState('');
  const [receivedAt, setReceivedAt] = useState(() => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 16));
  const [reason, setReason] = useState('');
  const [agreed, setAgreed] = useState(false);
  const expired = outstanding.some((r) => r.status !== 'held' || !r.holdUntil || Date.parse(r.holdUntil) <= Date.now());
  const amountCents = Math.round(Number(amount) * 100);
  return <Modal open lang="ar" dir="rtl" className="learning-plans-ar" title="تسجيل دفعة وتأكيد الحصص"
    description="سجّل دفعة تم استلامها والتحقق منها. يُؤكد هذا الإجراء حصص جميع الخطط المرتبطة."
    onClose={() => { if (!mutation.isPending) onClose(); }}>
    <form className="space-y-4" onSubmit={(e) => {
      e.preventDefault();
      const input: RecordPlanPaymentInput = { amountCents, currency: request.currency, method, reference: reference.trim(),
        receivedAt: new Date(`${receivedAt}:00+03:00`).toISOString(), reason: reason.trim(), familyAgreed: agreed };
      mutation.mutate({ path: `/admin/learning-plans/${request.id}/payments`, body: input }, { onSuccess: onDone });
    }}>
      <fieldset className="space-y-4" disabled={mutation.isPending}>
        <p className="font-medium">{request.studentName} · الإجمالي {(total / 100).toFixed(2)} {request.currency}</p>
        {outstanding.map((r) => <p key={r.id} className="text-sm">{r.offer.nameAr || r.offer.nameEn} · {(r.amountCents / 100).toFixed(2)} {r.currency} · {r.startDate} → {r.endDate}</p>)}
        {expired && <p role="alert" className="text-danger">الجدول غير جاهز أو انتهت مهلة الحجز. أعد تعيين الجدول قبل تسجيل الدفع.</p>}
        <label className="block text-sm">المبلغ المستلم ({request.currency})<input className="input" type="number" min="0" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
        {amountCents !== total && <p className="text-sm text-danger">يجب أن يساوي المبلغ إجمالي الخطط المرتبطة.</p>}
        <label className="block text-sm">طريقة الدفع<select className="input" value={method} onChange={(e) => setMethod(e.target.value === 'cash' ? 'cash' : 'bank_transfer')}><option value="bank_transfer">تحويل بنكي</option><option value="cash">نقدًا</option></select></label>
        <label className="block text-sm">مرجع التحويل أو رقم الإيصال<input className="input" required maxLength={120} value={reference} onChange={(e) => setReference(e.target.value)} /></label>
        <label className="block text-sm">تاريخ الاستلام بتوقيت الرياض<input className="input" required type="datetime-local" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} /></label>
        <label className="block text-sm">ملاحظات التحقق<textarea className="input" required maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />تحققت من استلام الدفعة ووافقت الأسرة على جميع المواعيد والمعلّمين</label>
      </fieldset>
      {mutation.isError && <p role="alert" className="text-danger">{learningPlanError(mutation.error)}</p>}
      <div className="flex gap-2"><button type="button" className="btn btn-secondary" disabled={mutation.isPending} onClick={onClose}>إغلاق</button>
        <button type="submit" className="btn btn-primary" disabled={mutation.isPending || expired || !agreed || !reference.trim() || !reason.trim() || !receivedAt || amountCents !== total}>{mutation.isPending ? 'جارٍ التسجيل…' : 'تسجيل الدفع وتأكيد الحصص'}</button></div>
    </form>
  </Modal>;
}
